import React, { useRef } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { ConfigProvider, Modal, Spin } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import { AiQuestionAndAnswer } from "lib/core_components/AiQuestionAndAnswer";
import { Scorecard_Result_Questions_Sources } from "lib/generated/app-service-gql/graphql";
import { useAskBidQuestion } from "lib/hooks/api/bid/bid_question/useAskBidQuestion";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { settingsRoutes } from "lib/routes";
import { sysPrimaryDefault } from "lib/themes/colors";
import { MidPaddingModal } from "lib/themes/MidPaddingModal";
import { EventData, EventNames, useTracking } from "lib/tracking";
import DocumentSources from "../DocumentSources";
import SuggestedBidQuestions from "./SuggestedBidQuestions";

import css from "./StotlesAiModal.module.scss";

type BidQaContent = {
  question: string;
  answer: string;
  sources: Scorecard_Result_Questions_Sources[];
};

type AIQuestionForm = {
  lastQuestion: string;
  qaPairs: BidQaContent[];
};

type Props = {
  bidId: string;
  procurementStageId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSelectDocument: (
    source: Scorecard_Result_Questions_Sources,
    trackingContext: EventNames,
    trackingData: EventData,
  ) => Promise<void>;
  documentDrawerOpen: boolean;
  noticeTitle: string | undefined;
};

export default function StotlesAiModal({
  bidId,
  procurementStageId,
  isOpen,
  onClose,
  onSelectDocument,
  documentDrawerOpen,
  noticeTitle,
}: Props) {
  const { logEvent } = useTracking();

  // Used to scroll to the bottom of the question list
  const containerRef = useRef<HTMLDivElement>(null);
  const { control, handleSubmit, setValue, watch } = useForm<AIQuestionForm>({
    defaultValues: {
      qaPairs: [],
      lastQuestion: "",
    },
  });

  const { lastQuestion } = watch();
  const { fields, append } = useFieldArray({
    control,
    name: "qaPairs", // This matches the defaultValues key
  });

  const scrollToBottomOfAiChat = () => {
    setTimeout(() => {
      containerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  const {
    mutate: askBidQuestion,
    isLoading: isAskingQuestion,
    reset: resetMutation,
  } = useAskBidQuestion({
    onSuccess: (data, variables) => {
      if (data.askBidQuestion.answer) {
        // Append new question-answer pair to the list
        append({
          question: variables.req.question,
          answer: data.askBidQuestion.answer,
          sources: data.askBidQuestion.sources ?? [],
        });
        setValue("lastQuestion", "");

        // Scroll to the bottom of the question list when question is answered
        scrollToBottomOfAiChat();
      }
    },
  });

  const handleQuestionSubmit = (question: string) => {
    if (question.length < 10) {
      return;
    }
    logEvent(EventNames.stotlesAiBidQuestionAsked, {
      "Action type": "Question asked",
      "Question asked": question,
    });

    askBidQuestion({
      req: { question, bidId, procurementStageId },
    });

    // Scroll to the bottom of the question list when question is asked
    scrollToBottomOfAiChat();
  };

  const closeModal = () => {
    onClose();
    resetMutation();
  };

  return (
    <ConfigProvider theme={MidPaddingModal}>
      <Modal
        title={
          <Flex flexDirection="row" justifyContent="space-between" gap={16}>
            <Flex gap={8} flexDirection="column">
              <Text h2>Ask Stotles AI</Text>
              <Text>
                Ask questions about the underlying tender documents you uploaded to this bid /
                no-bid report. Stotles AI will sort through the sources and provide clarity.
              </Text>
            </Flex>
            <CloseModalButton onClick={closeModal}>
              <UIcon icon="xClose" size={16} color="inherit" />
            </CloseModalButton>
          </Flex>
        }
        open={isOpen}
        bodyProps={{
          style: { padding: 0 },
        }}
        // When document drawer is open, make modal appear behind it
        zIndex={documentDrawerOpen ? 2 : undefined}
        footer={null}
        closable={false}
        maskClosable
        className={css.modal}
        onCancel={closeModal}
      >
        <form onSubmit={handleSubmit(({ lastQuestion }) => handleQuestionSubmit(lastQuestion))}>
          <div className={css.qaPairs} ref={containerRef}>
            {/* Render existing Q&A pairs */}
            {fields.map(({ question, answer, sources }, index) => (
              <span key={index}>
                <AiQuestionAndAnswer
                  key={index}
                  question={question}
                  answer={answer.replace(/\\n/g, "\n")}
                  saveQuestionComponent={
                    <SaveQuestionContainer>
                      <Flex gap={8} alignItems="center">
                        <UIcon icon="infoCircle" size={20} color={sysPrimaryDefault} />
                        <Text>Save this question for future reports?</Text>
                      </Flex>
                      <Button
                        as="a"
                        buttonType="tertiary"
                        target="_blank"
                        href={settingsRoutes.bidSelfServe}
                      >
                        Add bid / no-bid criteria
                      </Button>
                    </SaveQuestionContainer>
                  }
                />
                {sources && sources.length > 0 && (
                  <span className={css.sources}>
                    <DocumentSources
                      sources={sources}
                      onSelectDocument={(source) =>
                        onSelectDocument(source, EventNames.citationOpened, {
                          "Action type": "Link clicked",
                          "Criteria name": "Ad-hoc question",
                        })
                      }
                      noticeTitle={noticeTitle}
                    />
                  </span>
                )}
              </span>
            ))}

            {/* Show suggested questions if there are no Q&A pairs */}
            {fields.length === 0 && !isAskingQuestion && (
              <SuggestedBidQuestions
                onPromptClick={(question) => {
                  setValue("lastQuestion", question);
                  handleQuestionSubmit(question);
                }}
              />
            )}
            {/* Show the last question as a loading state if it's being asked */}
            {isAskingQuestion && <AiQuestionAndAnswer question={lastQuestion} answer={undefined} />}
          </div>
          {/* Used to scroll to the bottom of the question list */}
          <span style={{ visibility: "hidden" }} />
          <div className={css.inputContainer}>
            <TextArea
              autoSize
              autoFocus
              name="lastQuestion"
              label={""}
              control={control}
              className={css.questionInput}
              placeholder="Ask a question about the documents..."
              rules={{
                required: "Please enter a question",
                minLength: { value: 10, message: "Please enter a question" },
              }}
              disabled={isAskingQuestion}
              aria-label="Ask a question"
              onPressEnter={(e) => {
                if (e.shiftKey) {
                  // Allow newline when Shift + Enter is pressed
                  return;
                }
                // Prevent newline and trigger submission on Enter without Shift
                e.preventDefault();
                e.stopPropagation();
                handleQuestionSubmit(e.currentTarget.value);
              }}
            />
            {isAskingQuestion ? (
              <Spin />
            ) : (
              <Button
                buttonType="primary"
                type="submit"
                aria-label="Ask question"
                icon="arrowUp"
                iconProps={{ size: 16 }}
                // Make the button circular
                style={{ borderRadius: 800 }}
              />
            )}
          </div>

          <p className={css.disclaimer}>
            AI-generated responses may be inaccurate. Verify important information.
          </p>
        </form>
      </Modal>
    </ConfigProvider>
  );
}

const SaveQuestionContainer = styled(Flex)(({ theme }) => ({
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 4,
  justifyContent: "space-between",
  padding: "10px 16px",
  alignItems: "center",
}));

const CloseModalButton = styled.button(({ theme }) => ({
  display: "flex",
  position: "relative",
  alignItems: "flex-start",
  height: "fit-content",
  color: theme.colors.sysTextSecondary,
  backgroundColor: "transparent",
  border: "none",
  lineHeight: 0,
  cursor: "pointer",
  zIndex: 2,
  "&:hover": {
    color: theme.colors.sysTextBlack,
  },
}));
